import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const experiences = [
    {
      icon: 'ri-graduation-cap-line',
      title: 'Communication Science',
      subtitle: 'International Student, Universitas Islam Indonesia',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Marketing and Branding B2B Intern',
      subtitle: 'Telkom Indonesia (Internship, Dec–March 2025)',
      color: 'from-rose-500 to-rose-600'
    },
    {
      icon: 'ri-global-line',
      title: 'Passage to ASEAN – International Course on Creative Media',
      subtitle: 'International Course on Creative Media',
      color: 'from-purple-500 to-rose-500'
    },
    {
      icon: 'ri-search-line',
      title: 'Student Researcher',
      subtitle: 'Academic Research & Analysis',
      color: 'from-rose-500 to-purple-500'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl">
        <div className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-rose-500 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
              About Me, Clorentia
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-rose-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="relative">
                <div className="w-full h-96 rounded-2xl bg-gradient-to-br from-purple-100 to-rose-100 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-quill-pen-line text-8xl text-purple-400 mb-4"></i>
                    <p className="text-2xl font-semibold text-gray-600" style={{ fontFamily: '"Playfair Display", serif' }}>Clorentia Sherly</p>
                  </div>
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-purple-400 to-rose-400 rounded-2xl -z-10"></div>
              </div>
            </div>

            <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <p className="text-lg text-gray-700 leading-relaxed mb-6" style={{ fontFamily: '"Lora", serif' }}>
                Hello! I'm a writer with a deep passion for <strong>storytelling</strong>, <strong>creative expression</strong>, and <strong>digital content creation</strong>. I work across writing, blogging, web development, and publishing — shaping meaningful narratives that connect and resonate.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed" style={{ fontFamily: '"Lora", serif' }}>
                My journey combines academic excellence with practical experience, allowing me to craft stories that not only engage but also inspire action and emotional connection.
              </p>
            </div>
          </div>

          {/* Experience Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className={`group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${600 + index * 100}ms` }}
              >
                <div className={`w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${exp.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${exp.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{exp.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{exp.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
